<template>
  <verify-mfa
    v-if="mfaRequired"
    :mfa-error="mfaError"
    @mfaVerified="completeSignIn"
  />
  <div v-else>
    <v-form
      ref="form"
      v-model="valid"
      @keyup.enter.native="signIn"
    >
      <v-card elevation="0">
        <v-card-text>
          <v-text-field
            ref="email"
            v-model="email"
            :disabled="saving"
            :rules="[validateRequired, ...emailRules]"
            label="Email"
            type="email"
            autofocus
            required
            @blur="emailDirty = true"
          />
          <v-text-field
            v-model="password"
            :disabled="saving"
            :rules="[validateRequired, ...passwordRules]"
            label="Password"
            type="password"
            required
            @blur="passwordDirty = true"
          />
        </v-card-text>
        <v-card-actions>
          <v-btn
            :to="{ name: 'password-reset', query: { email } }"
            large
            text
          >
            Trouble signing in?
          </v-btn>
          <v-spacer />
          <v-btn
            :disabled="!canSubmit"
            :loading="saving"
            color="primary"
            large
            @click.stop="signIn"
          >
            Sign In
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
  </div>
</template>

<script>
import { auth } from '@/firebase'
import {
  AUTH_INVALID_EMAIL,
  AUTH_MULTI_FACTOR_AUTH_REQUIRED,
  AUTH_TOO_MANY_REQUESTS,
  AUTH_USER_NOT_FOUND,
  AUTH_WRONG_PASSWORD
} from '@/utils/auth'
import { validateRequired } from '@/utils/validationRules'
import VerifyMfa from '@/components/auth/mfa/verify/VerifyMfa'

const INVALID_SIGNIN_ERRORS = [
  AUTH_INVALID_EMAIL,
  AUTH_USER_NOT_FOUND,
  AUTH_WRONG_PASSWORD
]

export default {
  name: 'Auth',
  components: {
    VerifyMfa
  },
  emits: ['signIn'],
  data: function () {
    return {
      valid: false,
      email: null,
      emailDirty: false,
      emailValid: true,
      password: null,
      passwordDirty: false,
      saving: false,
      mfaRequired: false,
      mfaError: null
    }
  },
  computed: {
    emailRules() {
      var rules = []
      if (this.emailDirty && !this.emailValid) {
        rules.push("That's not a valid email address.")
      } else {
        rules.push(true)
      }
      return rules
    },
    passwordRules() {
      var rules = []
      if (this.passwordDirty && !this.password) {
        rules.push('Please enter your password')
      } else {
        rules.push(true)
      }
      return rules
    },
    canSubmit() {
      return this.valid && this.emailValid && !this.saving
    }
  },
  watch: {
    email: function () {
      this.emailValid = this.$refs.email.$refs.input.validity.valid
    }
  },
  beforeMount() {
    const email = this.$route.query.email
    if (email) {
      this.email = email
    }
  },
  methods: {
    validateRequired,
    async signIn() {
      if (!this.canSubmit) {
        return
      }
      try {
        this.saving = true
        const { user } = await auth.signInWithEmailAndPassword(
          this.email,
          this.password
        )
        this.completeSignIn(user)
      } catch (error) {
        this.handleError(error)
      } finally {
        this.saving = false
      }
    },
    handleError(error) {
      if (error.code === AUTH_MULTI_FACTOR_AUTH_REQUIRED) {
        this.handleMfaRequired(error)
        return
      }
      if (error.code === AUTH_TOO_MANY_REQUESTS) {
        this.$showErrorbar('Access to this account has been temporarily disabled due to many failed login attempts. You can immediately restore it by resetting your password or you can try again later.')
        return
      }
      if (INVALID_SIGNIN_ERRORS.includes(error.code)) {
        this.$showErrorbar('Either your email or password are incorrect')
        return
      }
      this.$showErrorbar('Unable to sign in. Please contact support.')
      throw error
    },
    handleMfaRequired(error) {
      this.mfaError = error
      this.mfaRequired = true
    },
    completeSignIn(user) {
      this.$emit('signIn', user)
    }
  }
}
</script>
